import { Space, Table, Tag, message } from "antd";
import { useNavigate } from "react-router";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { UseMutateAsyncFunction, useMutation, useQuery } from "react-query";
import { deleteSupplier, getSupplierById } from "../../api/supplier";
import { getCurrencyDropdown } from "../../api/currency";
import { getContactTypes } from "../../api/contactType";
import EditButton from "../UI/EditButton/editbutton";
import DeleteButton from "../UI/DeleteButton/deletebutton";
import TransactionButton from "../UI/TransactionButton/transactionbutton";
import DeleteModal from "../UI/ModalComponent/DeleteModal/deletemodal";
import { getCountriesDropdown } from "../../api/countries";
import { AxiosResponse } from "axios";
import { Pagination } from "./types";

interface SuppliersListProps {
  suppliers: any;
  setSuppliers: Dispatch<any>;
  isSupplierLoading: boolean;
  getSuppliersMutation: any;
  pagination: Pagination;
  setPagination: any;
  filter: boolean | undefined;
  search: string;
  tableKey: string;
  setTableKey: any;
}
interface SupplierInterface {
  id: string;
  name: string;
  phone: string;
  email: string;
  curr: string;
  isActive: boolean;
}

export const SuppliersList = ({
  suppliers,
  isSupplierLoading,
  getSuppliersMutation,
  setSuppliers,
  pagination,
  setPagination,
  filter,
  search,
  tableKey,
  setTableKey
}: SuppliersListProps) => {
  const navigate = useNavigate();
  const [t] = useTranslation("common");
  const [formatedCurrency, setFormatedCurrency] = useState<{}[]>();
  const [formatedContactType, setFormatedContactType] = useState<{}[]>();
  const [initialValues] = useState({ active: undefined });
  const [formatedCountry, setFormatedCountry] = useState<[]>();
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();

  const deleteSupplierMutation = useMutation("deleteSupplier", deleteSupplier, {
    onSuccess: async () => {
      const { data } = await getSuppliersMutation({})
      if (data.total !== pagination.total) {
        setPagination((prevPagination: any) => ({
          ...prevPagination,
          total: data.total,
          current: 1
        }));
        setTableKey((tableKey: any) => tableKey + 1);
      }
      setSuppliers(data?.data);
      message.success("Complete successfully");
    }
  });

  const { isLoading: loadingContactType } = useQuery(
    "contactTypes",
    getContactTypes,
    {
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        setFormatedContactType(
          res.data.map((contactType: any) => {
            return { value: contactType.id, label: contactType.typeName };
          })
        );
      },
    }
  );

  const { isLoading: loadingCurrency } = useQuery(
    "currencies",
    getCurrencyDropdown,
    {
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        setFormatedCurrency(
          res.data?.map((curr: any) => {
            return { value: curr.id, label: curr.name };
          })
        );
      },
    }
  );

  const { isLoading: loadingCountry } = useQuery(
    "countries",
    getCountriesDropdown,
    {
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        setFormatedCountry(
          res.data?.map((country: any) => {
            return { value: country.id, label: country.name };
          })
        );
      },
    }
  );

  const getSupplierMutation = useMutation("getSupplier", getSupplierById);

  function fillSupplierById(id: any) {
    getSupplierMutation.mutateAsync(id).then((res) => {
      navigate(`/suppliers/${id}`, {
        state: {
          supplier: res.data,
          initialValues: initialValues,
          loadingCountry: loadingCountry,
          formatedCountry: formatedCountry,
          formatedCurrency: formatedCurrency,
          loadingCurrency: loadingCurrency,
          formatedContactType: formatedContactType,
          loadingCopntactType: loadingContactType,
        },
      });
    });
  }

  const handleDelete = async (id: string | undefined, name: string) => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(name);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteSupplierMutation.mutate({ id: idToDelete });
    }
    setOpen(false);
  };

  const handleTableChange = async (pagination: any) => {
    setPagination(pagination);
    const res = await getSuppliersMutation({
      q: search,
      limit: pagination.limit,
      offset: (pagination.current - 1) * pagination.pageSize,
      isActive: filter
    })
    setSuppliers(res.data.data)
    if (res.data.total !== pagination.total) {
      setPagination((prevPagination: any) => ({
        ...prevPagination,
        total: res.data.total,
        current: 1
      }));
    }
  };

  useEffect(() => {
    getSuppliersMutation({
      q: search,
      limit: pagination.limit,
      offset: (pagination.current - 1) * pagination.pageSize,
      isActive: filter
    }).then((res: any) => {
      setSuppliers(res.data?.data);
      if (res.data.total !== pagination.total) {
        setPagination((prevPagination: Pagination) => ({
          ...prevPagination,
          total: res.data.total,
          current: 1
        }));
      }
    });
  }, [])

  const columns: ColumnsType<SupplierInterface> = [
    {
      title: `${t("name")}`,
      dataIndex: "name",
      key: "name",
      className: "table-header",
    },
    {
      title: `${t("phone-number")}`,
      dataIndex: "phone",
      key: "phone",
      render: (phone) => {
        if (phone) {
          const phoneNumberParts = phone.split("/");
          const phoneNumber =
            phoneNumberParts.length > 1 ? phoneNumberParts[1] : phone;
          return <span>{phoneNumber}</span>;
        }
        return null;
      },
    },
    {
      title: `${t("email")}`,
      dataIndex: "email",
      key: "email",
    },
    {
      title: `${t("currency")}`,
      dataIndex: "curr",
      key: "curr",
      render: (record: any) => {
        return record ? record.name : null;
      },
    },
    {
      title: `${t("notes")}`,
      dataIndex: "notes",
      key: "notes",
    },
    {
      title: `${t("status")}`,
      dataIndex: "isActive",
      align: "center",
      key: "isActive",
      width: "6rem",
      render: (record: any) =>
        record ? (
          <Tag className="active">{t("active")}</Tag>
        ) : (
          <Tag className="inactive">{t("inactive")}</Tag>
        ),
    },
    {
      title: `${t("action")}`,
      key: "action",
      fixed: "right",
      width: "7rem",
      align: "center",
      render: (record) => (
        <Space>
          <TransactionButton
            onClick={() => {
              navigate(`/supplierbalance`, {
                state: { id: record.id, name: record.name },
              });
            }}
          />
          <EditButton
            onClick={() => {
              fillSupplierById(record.id);
            }}
          />
          <DeleteButton
            onClick={() => handleDelete(record?.id, record?.name)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />
      <Table
        key={tableKey}
        scroll={{ x: 1000 }}
        loading={isSupplierLoading}
        columns={columns}
        dataSource={suppliers}
        rowClassName={(record: any, index: number) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        pagination={{ ...pagination, showSizeChanger: false }}
        onChange={handleTableChange}
        rowKey={(record) => record.id}
      />
    </>
  );
};
